import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ArrowUpRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
export function AirportsTransportation() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'Airports & Transportation'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-787">
      <Header data-id="element-788" />
      <main className="pt-[120px]" data-id="element-789">
        {/* SECTION 1: HERO */}
        <section className="pt-24 pb-28 px-6" data-id="element-790">
          <div className="max-w-3xl mx-auto" data-id="element-791">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-792">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-793">
                {isFR ? 'SECTEURS · AÉROPORTS & TRANSPORT' : 'INDUSTRIES · AIRPORTS & TRANSPORTATION'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-794">
                {isFR ? 'Une identité vérifiée. À la vitesse du voyage.' : 'Verified identity. At the speed of travel.'}
              </h1>
              <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug mb-10" data-id="element-795">
                {isFR ? "L'IA arrive dans les aéroports. La plupart des projets échoueront — parce que les données vivent en silos, éparpillées entre tableaux de bord, radios et feuilles de calcul qui ne se parlent pas." : "AI is arriving in airports. Most will fail — because the data lives in silos, scattered across dashboards, radios and spreadsheets that don't talk to each other."}
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-796">
                <Link to="/request-demo?industry=airports" onClick={() => track('Demo CTA Clicked', {
                location: 'Airports Hero',
                cta: 'Talk to us'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-797">
                  {isFR ? 'Parlez-nous' : 'Talk to us'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-798" />
                </Link>
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Airports Hero',
                cta: 'Request a Demo'
              })} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-799">
                  {isFR ? 'Demander une démo' : 'Request a Demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-800" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: RESEARCH SHOWS */}
        <section className="py-24 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-801">
          <div className="max-w-3xl mx-auto" data-id="element-802">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-8" data-id="element-803">
              {isFR ? 'La recherche montre' : 'Research Shows'}
            </motion.div>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-base text-[#121212]/55 leading-relaxed flex items-start gap-3 mb-8" data-id="element-804">
              <span className="mt-2 inline-block w-1.5 h-1.5 bg-[#121212]/30 shrink-0" aria-hidden="true" data-id="element-805" />
              <span data-id="element-806">
                {isFR ? 'Selon une ' : 'Per an '}
                <a href="https://www.oag.com/ai-aviation-operations" target="_blank" rel="noopener noreferrer" className="text-[#4F46E5] underline underline-offset-2 hover:text-[#4F46E5]/80 transition-colors" data-id="element-807">
                  {isFR ? "étude de l'IATA" : 'IATA study'}
                </a>
                {isFR ? ', 63 % des compagnies aériennes sont freinées par des silos opérationnels, et près de la moitié des retards (47 %) découlent d’une mauvaise coordination entre les fonctions.' : ', 63% of airlines are held back by operational silos, and nearly half of delays (47%) stem from poor coordination between functions.'}
              </span>
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="border-t border-[#E4E6EC] pt-6 text-xs text-[#121212]/40 leading-relaxed" data-id="element-808">
              {isFR ? 'Contexte de marché. Pas des résultats de déploiement BCITI. Source : IATA, via OAG (2025).' : 'Market context. Not BCITI deployment results. Source: IATA, via OAG (2025).'}
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: BCITI ANSWER */}
        <section className="py-28 px-6 border-t border-[#E4E6EC]" data-id="element-809">
          <div className="max-w-3xl mx-auto" data-id="element-810">
            <motion.p {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-811">
              {isFR ? "L'IA ne règle pas les silos — elle a besoin d'une fondation unifiée. L'identité vérifiée relie chaque point de contact sur une seule base. C'est ce qui fait durer l'intelligence." : "AI doesn't fix silos — it needs a unified foundation. Verified identity connects every touchpoint on one base. That's what makes intelligence last."}
            </motion.p>
          </div>
        </section>
      </main>
      <Footer data-id="element-812" />
    </div>;
}